import { useState } from "react";
import axios from "axios";
import { useAccountant } from "@/contexts/AccountantContext";
import { todayInputDate } from "@/lib/formatDate";
import { useKipaToast } from "@/components/kipa";
import KipaIcon from "@/components/kipa/KipaIcon";
import { getQuickReplyDisplay } from "@/lib/quickReplyIcons";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Record payment against an unpaid/partial invoice.
 * Updates cash/bank balances and stores payment date on the invoice.
 */
export default function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const { fmt } = useAccountant();
  const { showToast } = useKipaToast();
  const balance = invoice.total - (invoice.amount_paid || 0);
  const [mode, setMode] = useState("full");
  const [amount, setAmount] = useState(balance);
  const [accountType, setAccountType] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => todayInputDate());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    if (!accountType) {
      setError("Select Cash or Bank — where did the money go?");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        account_type: accountType,
        payment_date: paymentDate || undefined,
      };
      if (mode === "partial") {
        const amt = Number(amount);
        if (!amt || amt <= 0) {
          setError("Enter a valid payment amount.");
          setSaving(false);
          return;
        }
        if (amt > balance) {
          setError("Amount cannot exceed the outstanding balance.");
          setSaving(false);
          return;
        }
        payload.amount = amt;
      }
      await axios.post(`${API}/invoices/${invoice.id}/collect-payment`, payload);
      showToast(`Payment recorded for ${invoice.invoice_number}`, "success");
      onSaved?.(invoice);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not record payment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45"
      onClick={onClose}
      data-testid="payment-modal"
    >
      <div
        className="w-full max-w-[430px] bg-white rounded-t-3xl p-5 pb-6"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.25s ease-out" }}
      >
        <div className="w-12 h-1.5 bg-[var(--br2)] rounded-full mx-auto mb-4" />

        <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)]">
          Record payment
        </div>
        <div className="text-[18px] font-black text-[var(--tx)] mb-1">{invoice.invoice_number}</div>
        <div className="text-[12px] text-[var(--tx2)] mb-4">
          {invoice.customer} · {invoice.item} · Total {fmt(invoice.total)}
        </div>

        <div className="bg-[var(--gdl)] border border-[var(--gdd)] rounded-xl p-3 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--go)]">
            Balance outstanding
          </div>
          <div className="text-[20px] font-black text-[var(--go)]">{fmt(balance)}</div>
        </div>

        <div className="mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">
            Payment type
          </div>
          <div className="flex gap-2">
            {[
              { id: "full" },
              { id: "partial" },
            ].map((opt) => {
              const { icon: ChipIcon, label } = getQuickReplyDisplay(opt);
              return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  setMode(opt.id);
                  if (opt.id === "full") setAmount(balance);
                }}
                className={`chip flex-1 inline-flex items-center justify-center gap-1.5 ${mode === opt.id ? "chip-active" : ""}`}
              >
                {ChipIcon && (
                  <KipaIcon
                    icon={ChipIcon}
                    size="sm"
                    color={mode === opt.id ? "#fff" : "var(--color-primary)"}
                  />
                )}
                {label}
              </button>
            );
            })}
          </div>
        </div>

        {mode === "partial" && (
          <div className="mb-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">
              Amount received now
            </div>
            <input
              type="number"
              step="any"
              className="s-inp"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payment-amount"
            />
          </div>
        )}

        <div className="mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">
            Payment date
          </div>
          <input
            type="date"
            className="s-inp"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            data-testid="payment-date"
          />
        </div>

        <div className="mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">
            Received into
          </div>
          <div className="flex gap-2">
            {[
              { id: "cash" },
              { id: "bank" },
            ].map((opt) => {
              const { icon: ChipIcon, label } = getQuickReplyDisplay(opt);
              return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setAccountType(opt.id)}
                data-testid={`payment-account-${opt.id}`}
                className={`chip flex-1 inline-flex items-center justify-center gap-1.5 ${accountType === opt.id ? "chip-active" : ""}`}
              >
                {ChipIcon && (
                  <KipaIcon
                    icon={ChipIcon}
                    size="sm"
                    color={accountType === opt.id ? "#fff" : "var(--color-primary)"}
                  />
                )}
                {label}
              </button>
            );
            })}
          </div>
        </div>

        {error && (
          <div className="text-[12px] text-[var(--rd)] font-semibold mb-2">{error}</div>
        )}

        <button
          onClick={confirm}
          disabled={saving}
          data-testid="payment-confirm"
          className="w-full bg-[var(--gn)] text-white rounded-2xl py-3 font-extrabold text-[14px] mb-2 disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {saving ? "Saving…" : (
            <>
              <KipaIcon icon={CheckCircle2} size="sm" color="#fff" />
              Confirm payment
            </>
          )}
        </button>
        <button
          onClick={onClose}
          className="w-full text-[var(--tx3)] rounded-2xl py-2 text-[12px]"
        >
          Cancel
        </button>

        <style>{`
          .s-inp {
            background: var(--sf2);
            border: 1.5px solid var(--br);
            border-radius: 12px;
            padding: 12px 14px;
            color: var(--tx);
            font-size: 16px;
            width: 100%;
            font-family: inherit;
            outline: none;
          }
          .s-inp:focus { border-color: var(--ac); }
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}
